import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, ExternalLink, Clock, ChevronRight } from 'lucide-react';

export default function Footer() {
  const socialLinks = [
    { icon: Linkedin, href: "https://www.linkedin.com/company/unified-connexions-inc-/", label: "LinkedIn" },
    { icon: Twitter, href: "https://x.com/ConneXionsDFW/", label: "X (Twitter)" },
    { icon: Facebook, href: "https://www.facebook.com/unifiedconnexions/", label: "Facebook" }
  ];

  const footerLinks = {
    solutions: {
      title: "Solutions",
      links: [
        { label: "Managed IT", href: "/services/managed-it" },
        { label: "Co-Managed IT", href: "/services/co-managed-it" },
        { label: "Professional Services", href: "/services/professional-services" },
        { label: "Consulting Services", href: "/services/consulting" }
      ]
    },
    company: {
      title: "Company",
      links: [
        { label: "About Us", href: "/about" },
        { label: "Leadership", href: "/leadership" },
        { label: "Careers", href: "/careers" },
        { label: "Contact", href: "/contact" }
      ]
    },
    support: {
      title: "Support",
      links: [
        { label: "Remote Support", href: "/remote-support" },
        { label: "Monitoring Portal", href: "https://ucx.logicmonitor.com", external: true }
      ]
    },
    legal: {
      title: "Legal",
      links: [
        { label: "Privacy Policy", href: "/privacy-policy" },
        { label: "Terms & Conditions", href: "/terms-conditions" },
        { label: "Cookie Policy", href: "/cookie-policy" }
      ]
    }
  };

  return (
    <footer className="relative bg-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12 grid grid-cols-1 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Company Info - Takes 2 columns */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <Link to="/" className="inline-block">
                <img 
                  src="https://i.ibb.co/QpjHXgM/UCXLogo.png" 
                  alt="UCX Logo" 
                  className="h-10 w-auto"
                />
              </Link>
              <p className="mt-6 text-gray-600 leading-relaxed">
                Your trusted partner in IT management solutions, delivering enterprise-grade technology services with unmatched expertise.
              </p>
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                  aria-label={social.label}
                >
                  <div className="bg-gray-50 p-2 rounded-lg text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                    <social.icon className="h-5 w-5" />
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links - Takes 3 columns */}
          <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-3 gap-8">
            {Object.entries(footerLinks).slice(0, 3).map(([key, section]) => (
              <div key={key}>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                  {section.title}
                </h3>
                <ul className="space-y-3">
                  {section.links.map((link, index) => (
                    <li key={index}>
                      {link.external ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-500 hover:text-blue-600 transition-colors flex items-center group"
                        >
                          <span className="text-sm">{link.label}</span>
                          <ExternalLink className="ml-1 h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </a>
                      ) : (
                        <Link
                          to={link.href}
                          className="text-gray-500 hover:text-blue-600 transition-colors text-sm"
                        >
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Contact Info - Takes 1 column */}
          <div className="lg:col-span-1">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              Contact Us
            </h3>
            <div className="space-y-4">
              <a href="tel:844-219-3935" className="flex items-center text-gray-500 hover:text-blue-600 transition-colors">
                <Phone className="h-4 w-4 mr-2" />
                <span className="text-sm">844-219-3935</span>
              </a>
              <a href="mailto:sales@ucx.co" className="flex items-center text-gray-500 hover:text-blue-600 transition-colors">
                <Mail className="h-4 w-4 mr-2" />
                <span className="text-sm">sales@ucx.co</span>
              </a>
              <div className="flex items-start text-gray-500">
                <MapPin className="h-4 w-4 mr-2 mt-1" />
                <div className="text-sm">
                  <p>1348 Hwy 287, Suite 300</p>
                  <p>Waxahachie, TX 75165</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-gray-100">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-wrap justify-center md:justify-start gap-x-6 gap-y-2 text-sm text-gray-500">
              <span>© 2024 Unified ConneXions</span>
              {footerLinks.legal.links.map((link, index) => (
                <Link
                  key={index}
                  to={link.href}
                  className="hover:text-blue-600 transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <div>
              <p className="text-sm text-gray-500">
                Powered By{' '}
                <a
                  href="https://aikarotech.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 transition-colors"
                >
                  Aikarotech
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}